/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import * as Dialog from '@radix-ui/react-dialog'
import React from 'react'
import { MdClose } from 'react-icons/md'

import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type GenericDialogProps = {
  /** Whether the dialog is visible. */
  open: boolean

  /** Callback invoked when this dialog is triggered to be closed. */
  handleClose: () => void

  /** Optional class name to apply to the dialog content. */
  className?: string

  /** Optional toggle to show the close button. */
  showClose?: boolean

  /** Optional toggle to enable the dialog to be closed by clicking outside the dialog (default: true). */
  closeOnOutsideClick?: boolean

  /** Optional toggle to enable the dialog to be closed by pressing the escape key (default: true). */
  closeOnEscapeKey?: boolean

  /** Optional children to render inside the dialog panel. */
  children?: React.ReactNode
}

/**
 * Dialog wrapper around {@link Dialog} from Radix that provides generic styling and functionality.
 */
const GenericDialog: React.FC<GenericDialogProps> = ({
  open,
  handleClose,
  className,
  showClose = true,
  closeOnEscapeKey = true,
  closeOnOutsideClick = true,
  children,
}) => {
  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={cn('p-6 bg-black/40 fixed inset-0 z-max grid place-items-center overflow-y-auto')}>
          <Dialog.Content
            className={cn(
              'flex flex-col bg-white z-10 rounded-2xl p-6 border-opacity-20 border border-grey-50',
              'lg:p-10',
              className,
            )}
            onInteractOutside={closeOnOutsideClick ? handleClose : undefined}
            onEscapeKeyDown={closeOnEscapeKey ? handleClose : undefined}
            onOpenAutoFocus={(event) => event.preventDefault()}
          >
            {showClose && <MdClose className={'text-graphite-500 cursor-pointer self-end'} onClick={handleClose} />}
            {children}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

export { GenericDialog }
export type { GenericDialogProps }
